import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Eye, Pencil, MoreHorizontal, FileText, Calendar, User, Building2, DollarSign, Activity, Settings, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useEditarCotizacion, useMarcarAtendida } from '../hooks/usePricing'
import { CotizacionDetailModal } from './CotizacionDetailModal'
import { CotizacionEditModal } from './CotizacionEditModal'
import { CotizacionAtendidaModal } from './CotizacionAtendidaModal'
import type { PaginatedCotizaciones, CotizacionListItem, CotizacionFilters, EditarCotizacionPayload } from '../types'

interface CotizacionesTableProps {
  data?: PaginatedCotizaciones
  isLoading: boolean
  filters: CotizacionFilters
  onFiltersChange: (filters: CotizacionFilters) => void
}

export function CotizacionesTable({ data, isLoading, filters, onFiltersChange }: CotizacionesTableProps) {
  const [detailCot, setDetailCot] = useState<CotizacionListItem | null>(null)
  const [editCot, setEditCot] = useState<CotizacionListItem | null>(null)
  const [atendidaCot, setAtendidaCot] = useState<CotizacionListItem | null>(null)

  const editarMutation = useEditarCotizacion()
  const marcarMutation = useMarcarAtendida()

  const page = filters.page || 1
  const pageSize = filters.page_size || 25

  const handleEditar = (payload: EditarCotizacionPayload) => {
    if (!editCot) return
    editarMutation.mutate(
      { id: editCot.id, payload },
      { onSuccess: () => setEditCot(null) }
    )
  }

  const handleMarcar = (payload: { atendida: boolean; notas?: string }) => {
    if (!atendidaCot) return
    marcarMutation.mutate(
      { id: atendidaCot.id, payload },
      { onSuccess: () => setAtendidaCot(null) }
    )
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </Card>
    )
  }

  if (!data || data.results.length === 0) {
    return (
      <Card className="p-12 text-center">
        <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-semibold">No se encontraron cotizaciones</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Ajusta los filtros o espera nuevas solicitudes
        </p>
      </Card>
    )
  }

  return (
    <>
      <div className="rounded-md border border-gray-300 dark:border-gray-700">
        <Table>
          <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,22%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,22%)]">
              <TableRow>
                <TableHead><div className="flex items-center gap-2"><Calendar className="h-4 w-4" />Fecha</div></TableHead>
                <TableHead><div className="flex items-center gap-2"><User className="h-4 w-4" />Contacto</div></TableHead>
                <TableHead><div className="flex items-center gap-2"><Building2 className="h-4 w-4" />Laboratorio</div></TableHead>
                <TableHead className="text-right"><div className="flex items-center justify-end gap-2"><DollarSign className="h-4 w-4" />Total</div></TableHead>
                <TableHead className="text-center"><div className="flex items-center justify-center gap-2"><Activity className="h-4 w-4" />Estado</div></TableHead>
                <TableHead className="text-center"></TableHead>
                <TableHead className="text-right"><div className="flex items-center justify-end gap-2"><Settings className="h-4 w-4" />Acciones</div></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.results.map((cot) => (
                <TableRow key={cot.id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(cot.fecha_creacion).toLocaleDateString('es-EC')}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{cot.nombre}</p>
                      <p className="text-sm text-muted-foreground">{cot.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{cot.laboratorio || '—'}</TableCell>
                  <TableCell className="text-right font-medium">
                    ${cot.precio_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={cot.atendida
                      ? 'badge-gradient hover:opacity-90'
                      : 'bg-transparent border border-amber-500 text-amber-500 hover:bg-transparent hover:text-amber-500'
                    }>
                      {cot.atendida ? 'Atendida' : 'Pendiente'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={cot.atendida}
                      onCheckedChange={(checked) => {
                        marcarMutation.mutate({ id: cot.id, payload: { atendida: checked } })
                      }}
                      disabled={marcarMutation.isPending}
                      className="data-[state=checked]:bg-teal-500"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setDetailCot(cot)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditCot(cot)}>
                            <Pencil className="mr-2 h-4 w-4" /> Editar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Mostrar</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onFiltersChange({ ...filters, page: 1, page_size: parseInt(value) })}
          >
            <SelectTrigger className="w-[80px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">por página</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onFiltersChange({ ...filters, page: 1 })}
            disabled={page === 1}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onFiltersChange({ ...filters, page: page - 1 })}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[100px] text-center">
            {page} de {data.total_pages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onFiltersChange({ ...filters, page: page + 1 })}
            disabled={page === data.total_pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onFiltersChange({ ...filters, page: data.total_pages })}
            disabled={page === data.total_pages}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <CotizacionDetailModal
        cotizacion={detailCot}
        open={!!detailCot}
        onOpenChange={(open) => { if (!open) setDetailCot(null) }}
      />

      <CotizacionEditModal
        cotizacion={editCot}
        open={!!editCot}
        onOpenChange={(open) => { if (!open) setEditCot(null) }}
        onSubmit={handleEditar}
        isSubmitting={editarMutation.isPending}
      />

      <CotizacionAtendidaModal
        cotizacion={atendidaCot}
        open={!!atendidaCot}
        onOpenChange={(open) => { if (!open) setAtendidaCot(null) }}
        onSubmit={handleMarcar}
        isSubmitting={marcarMutation.isPending}
      />
    </>
  )
}
